import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(customParseFormat);

import { Card, Input, Select, Table } from "@stonelog/stonelog-design-system";
import { DatePickerCustom } from "@components/DatePickerCustom";
import { usePaginationConfig } from "@hooks/usePaginationConfig";
import { GingaIcon } from "@stonelog/stonelog-ginga-icons";
import { compareTableColumn } from "../../../../utils/common";

import { hubsSelector } from "../../../../features/senninha/hubsSlice";
import { getAttachmentStoneEntrega, attachmentSelector, resetAttachmentStoneEntregaTableData } from "../../../../features/anexos/attachmentSlice";
import AttachmentStoneEntregaDrawer from "./AttachmentStoneEntregaDrawer";

import * as S from "./styles";

const AttachmentStoneEntrega = () => {
    const dispatch = useDispatch();

    const { allHubs, allHubsStatus } = useSelector(hubsSelector);
    const { getAttachmentStoneEntregaTableDataStatus, getAttachmentStoneEntregaTableData } = useSelector(attachmentSelector);

    const [poloSelected, setPoloSelected] = useState(null);
    const [dateSelected, setDateSelected] = useState(null);
    const [pedidoSelected, setPedidoSelected] = useState(null);
    const [clienteSelected, setClienteSelected] = useState(null);
    const [tecnicoSelected, setTecnicoSelected] = useState(null);
    const [searchTrackingInput, setSearchTrackingInput] = useState("");

    const [open, setOpen] = useState(false);
    const [rowClickedInfos, setRowClickedInfos] = useState({});

    const paginationConfig = usePaginationConfig([10, 10]);

    useEffect(() => {
        dispatch(resetAttachmentStoneEntregaTableData());
    }, [dispatch]);

    useEffect(() => {
        if (dateSelected && poloSelected) {
            const formattedDate = dateSelected.format("YYYY-MM-DD");
            const selectedPoloObj = allHubs?.find(p => p.id === poloSelected);
            const poloName = selectedPoloObj ? selectedPoloObj.name : null;

            if (poloName) {
                setPedidoSelected(null);
                setClienteSelected(null);
                setTecnicoSelected(null);
                setSearchTrackingInput("");

                dispatch(getAttachmentStoneEntrega({
                    dataOs: formattedDate,
                    poloName: poloName
                }));
            }
        }
    }, [dispatch, dateSelected, poloSelected]);

    const handleSearchByTracking = () => {
        const trimmed = searchTrackingInput.trim();
        if (!trimmed) return;

        setPoloSelected(null);
        setDateSelected(null);
        setPedidoSelected(null);
        setClienteSelected(null);
        setTecnicoSelected(null);

        dispatch(getAttachmentStoneEntrega({ trackingCode: trimmed }));
    };

    const columns = [
        {
            title: 'Número do pedido',
            dataIndex: 'tracking_code',
            key: 'tracking_code',
            sorter: {
                compare: (a, b) => compareTableColumn(a, b, "tracking_code"),
            },
        },
        {
            title: 'Nome do EC',
            dataIndex: 'sender_name',
            key: 'sender_name',
            sorter: {
                compare: (a, b) => compareTableColumn(a, b, "sender_name"),
            },
        },
        {
            title: "Nome do cliente",
            dataIndex: "recipient_name",
            key: "recipient_name",
            sorter: {
                compare: (a, b) => compareTableColumn(a, b, "recipient_name"),
            },
        },
        {
            title: 'Data de última atualização',
            dataIndex: 'updated_at_formatted',
            key: 'updated_at',
            sorter: {
                compare: (a, b) => compareTableColumn(a, b, "updated_at"),
            },
        }
    ];

    const pedidoOptions = useMemo(() => {
        if (!getAttachmentStoneEntregaTableData || getAttachmentStoneEntregaTableData.length === 0) return [];

        const uniqueCodes = [...new Set(getAttachmentStoneEntregaTableData.map(item => item.tracking_code))];
        return uniqueCodes.map(code => ({ value: code, label: String(code) }));
    }, [getAttachmentStoneEntregaTableData])

    const tecnicoOptions = useMemo(() => {
        if (!getAttachmentStoneEntregaTableData || getAttachmentStoneEntregaTableData.length === 0) return [];

        const seen = new Set();
        const uniqueTecnicos = [];

        getAttachmentStoneEntregaTableData.forEach(item => {
            if (item.angel_responsavel && !seen.has(item.angel_responsavel)) {
                seen.add(item.angel_responsavel);
                uniqueTecnicos.push({ value: item.angel_responsavel, label: String(item.angel_responsavel) });
            }
        });

        return uniqueTecnicos;
    }, [getAttachmentStoneEntregaTableData]);

    const dataSource = useMemo(() => {
        if (!getAttachmentStoneEntregaTableData || getAttachmentStoneEntregaTableData.length === 0) return [];

        let filteredData = [...getAttachmentStoneEntregaTableData];

        if (pedidoSelected) {
            filteredData = filteredData.filter(item => item.tracking_code === pedidoSelected);
        }

        if (clienteSelected) {
            filteredData = filteredData.filter(item => item.recipient_name === clienteSelected);
        }

        if (tecnicoSelected) {
            filteredData = filteredData.filter(item => item.angel_responsavel === tecnicoSelected);
        }

        const uniqueData = Array.from(
            new Map(
                filteredData.map(item => [item.tracking_code, item])
            ).values()
        );

        return uniqueData
            .sort((a, b) => dayjs(b.updated_at).diff(dayjs(a.updated_at)))
            .map((item, idx) => ({
                ...item,
                key: idx,
                updated_at_formatted: item.updated_at
                    ? dayjs(item.updated_at).format('DD/MM/YYYY')
                    : '-',
            }));
    }, [getAttachmentStoneEntregaTableData, pedidoSelected, clienteSelected, tecnicoSelected]);

    const poloOptions = useMemo(() => {
        if (!allHubs) return [];
        return allHubs.map(polo => ({ value: polo.id, label: polo.name }));
    }, [allHubs]);

    return (
        <>
            <AttachmentStoneEntregaDrawer
                open={open}
                setOpen={setOpen}
                rowClickedInfos={rowClickedInfos}
            />
            <Card.Root>
                <Card.Body className="ginga-lib-card-body">
                    <Card.Header>
                        <Card.HeaderText
                            title="Anexos Stone Entrega"
                            subtitle="Acompanhe os atendimentos e, para visualizar os detalhes do comprovante, clique no pedido desejado."
                        />
                    </Card.Header>
                    <S.InventaryFilterContainer>
                        <S.TopInventaryFilterContent>
                            <Input
                                size="small"
                                label="Buscar pedido"
                                value={searchTrackingInput}
                                onChange={(e) => setSearchTrackingInput(e.target.value)}
                                onPressEnter={handleSearchByTracking}
                                placeholder="Digite o tracking code"
                                clear
                            />
                            <Select
                                prefixIcon={<GingaIcon name="location" color="rgba(118, 127, 141, 1)" size={21} />}
                                placeholder="Selecione um polo"
                                label="Polo"
                                size="small"
                                value={poloSelected}
                                onChange={(val) => {
                                    setPoloSelected(val);
                                    if (!val) {
                                        setPedidoSelected(null);
                                        setClienteSelected(null);
                                        setTecnicoSelected(null);
                                    }
                                }}
                                options={poloOptions}
                                disabled={allHubsStatus === 'pending'}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                className="ginga-lib-select-table"
                            />
                            <DatePickerCustom
                                label={"Data de atualização"}
                                value={dateSelected}
                                onChange={(date) => setDateSelected(date)}
                                format={"DD/MM/YYYY"}
                                placeholder={"DD/MM/AAAA"}
                                allowClear
                            />
                            <Select
                                placeholder="Filtrar por Técnico"
                                label="Técnico"
                                size="small"
                                value={tecnicoSelected}
                                onChange={(val) => setTecnicoSelected(val)}
                                options={tecnicoOptions}
                                disabled={!getAttachmentStoneEntregaTableData?.length}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                className="ginga-lib-select-table"
                                suffixIcon={<GingaIcon name="chevron-down" color="rgba(118, 127, 141, 1)" size={21} />}
                            />
                        </S.TopInventaryFilterContent>
                    </S.InventaryFilterContainer>
                    <Table
                        paginationConfig={paginationConfig}
                        columns={columns}
                        dataSource={dataSource}
                        loading={getAttachmentStoneEntregaTableDataStatus === 'pending'}
                        className="ginga-lib-table"
                        onRow={(row) => ({
                            onClick: () => {
                                setRowClickedInfos(row);
                                setOpen(true);
                            },
                        })}
                    />
                </Card.Body>
            </Card.Root>
        </>
    );
};

export default AttachmentStoneEntrega;
